import React from "react";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { Chip, Button, Box, Typography, Card } from "@mui/material";
import { Link } from "react-router-dom";
import { getViolationStatusDetails } from "./violation.utils";

const pageCardSx = {
  borderRadius: "18px",
  border: "1px solid rgba(47,99,173,0.08)",
  boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
  backgroundColor: "rgba(255,255,255,0.96)",
};

const secondaryButtonSx = {
  borderRadius: "10px",
  textTransform: "none",
  fontWeight: 600,
  px: 1.5,
  borderColor: "rgba(47,99,173,0.18)",
  color: "#223142",
  whiteSpace: "nowrap",
  "&:hover": {
    borderColor: "#2f63ad",
    color: "#2f63ad",
    backgroundColor: "rgba(47,99,173,0.04)",
  },
};

const ViolationTable = ({ violations = [], violationTypes = [], statusMeta = [] }) => {
  const getViolationType = (typeId) =>
    violationTypes.find((t) => String(t.id) === String(typeId));

  const formatDate = (dateStr) =>
    dateStr ? String(dateStr).slice(0, 10) : "N/A";

  const resolveColor = (theme, color) => {
    if (color && theme?.palette?.[color]?.main) {
      return theme.palette[color].main;
    }

    if (typeof color === "string") {
      const trimmed = color.trim().toLowerCase();

      // avoid unreadable/light invalid values
      if (!trimmed || trimmed === "light" || trimmed === "inherit" || trimmed === "transparent") {
        return "#000";
      }

      return color;
    }

    return "#000";
  };

  const columns = [
    {
      field: "id",
      headerName: "Violation ID",
      width: 160,
      renderCell: (params) =>
        params?.row?.id ? (
          <Button
            component={Link}
            to={`${params.row.id}`}
            variant="text"
            size="small"
            sx={{
              textTransform: "none",
              fontWeight: 700,
              color: "#2f63ad",
              px: 0.5,
              minWidth: "auto",
              "&:hover": {
                backgroundColor: "rgba(47,99,173,0.04)",
              },
            }}
          >
            VLT-{params.row.id}
          </Button>
        ) : (
          "N/A"
        ),
    },
    {
      field: "property_id",
      headerName: "Property ID",
      width: 140,
      renderCell: (params) => params?.row?.property_id || "N/A",
    },
    {
      field: "member",
      headerName: "Member Name",
      width: 200,
      renderCell: (params) => params?.row?.member?.fullname || "N/A",
    },
    {
      field: "status",
      headerName: "Status",
      width: 200,
      renderCell: (params) => {
        const status = params?.row?.status || "";
        const { color, Icon, label } = getViolationStatusDetails(status, statusMeta);

        return (
          <Chip
            icon={
              Icon ? (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Icon sx={{ color: "#fff", fontSize: 18 }} />
                </Box>
              ) : undefined
            }
            label={label?.toUpperCase() || "UNKNOWN"}
            size="small"
            sx={{
              backgroundColor: (theme) => resolveColor(theme, color),
              color: "#fff",
              fontWeight: 700,
              borderRadius: "999px",
              "& .MuiChip-icon": {
                color: "#fff",
              },
            }}
          />
        );
      },
    },
    {
      field: "memberType",
      headerName: "Membership Type",
      width: 180,
      renderCell: (params) => params?.row?.member?.ClubMembershipTypeCode || "N/A",
    },
    {
      field: "full_address",
      headerName: "Address",
      flex: 1,
      minWidth: 250,
      renderCell: (params) => params?.row?.member?.full_address || "N/A",
    },
    {
      field: "cure_by_user",
      headerName: "Created By (User)",
      width: 220,
      renderCell: (params) => {
        const user = params?.row?.cure_by_user;
        return user ? (
          <Box sx={{ py: 0.5 }}>
            <Typography variant="body2" noWrap sx={{ color: "#223142", fontWeight: 600 }}>
              {user.name}
            </Typography>
            <Typography
              variant="caption"
              noWrap
              sx={{ color: "#6b778c", fontStyle: "italic" }}
            >
              {user.email}
            </Typography>
          </Box>
        ) : (
          "N/A"
        );
      },
    },
    {
      field: "ccr_violation_type_id",
      headerName: "Violation Type",
      flex: 1,
      minWidth: 250,
      renderCell: (params) => {
        const type = getViolationType(params?.row?.ccr_violation_type_id);
        if (!type) return "Unknown Type";

        return (
          <Box sx={{ py: 0.5 }}>
            <Typography variant="body2" fontWeight={700} noWrap sx={{ color: "#223142" }}>
              {type.name}
            </Typography>
            {type.rule && (
              <Typography
                variant="caption"
                noWrap
                sx={{ color: "#6b778c", fontStyle: "italic" }}
              >
                {type.rule}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: "violated_at",
      headerName: "Date",
      width: 150,
      renderCell: (params) => formatDate(params?.row?.violated_at),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Button
          component={Link}
          to={`${params?.row?.id}`}
          variant="outlined"
          size="small"
          sx={secondaryButtonSx}
        >
          View Details
        </Button>
      ),
    },
  ];

  return (
    <Card sx={{ ...pageCardSx, overflow: "hidden" }}>
      <Box
        sx={{
          px: { xs: 1.5, sm: 2, md: 3 },
          py: 2,
          borderBottom: "1px solid rgba(47,99,173,0.08)",
          backgroundColor: "rgba(47,99,173,0.02)",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
          Violations Table
        </Typography>
        <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.5 }}>
          Review and manage violations in a detailed table view.
        </Typography>
      </Box>

      <Box sx={{ p: { xs: 1.5, sm: 2, md: 2.5 } }}>
        <Box sx={{ height: 600, width: "100%" }}>
          <DataGrid
            rows={Array.isArray(violations) ? violations : []}
            columns={columns}
            getRowId={(row) => row.id}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 10, page: 0 },
              },
            }}
            pageSizeOptions={[10, 20, 50]}
            disableRowSelectionOnClick
            slots={{
              toolbar: GridToolbar,
            }}
            sx={{
              border: "1px solid rgba(47,99,173,0.08)",
              borderRadius: "14px",
              backgroundColor: "#fff",
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "rgba(47,99,173,0.04)",
                color: "#223142",
                fontWeight: 700,
                borderBottom: "1px solid rgba(47,99,173,0.08)",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: 700,
              },
              "& .MuiDataGrid-row": {
                borderBottom: "1px solid rgba(47,99,173,0.06)",
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: "rgba(47,99,173,0.03)",
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "none",
                alignItems: "center",
              },
              "& .MuiDataGrid-toolbarContainer": {
                padding: "12px 12px 8px 12px",
                borderBottom: "1px solid rgba(47,99,173,0.08)",
                backgroundColor: "rgba(255,255,255,0.9)",
              },
              "& .MuiButton-root": {
                textTransform: "none",
              },
              "& .MuiDataGrid-footerContainer": {
                borderTop: "1px solid rgba(47,99,173,0.08)",
                backgroundColor: "rgba(255,255,255,0.92)",
              },
            }}
          />
        </Box>
      </Box>
    </Card>
  );
};

export default ViolationTable;